import { CheckCircle, AlertTriangle, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ImportResponse, SkippedReason } from "@/services/cav-offline-sync/types";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { generateSkippedCSV, downloadFile } from "../utils/csv-export";

interface ImportResultsContentProps {
  result: ImportResponse;
}

const reasonLabels: Record<SkippedReason, string> = {
  not_found: "Nao encontrada",
  already_processed: "Ja processada",
  invalid_data: "Dados invalidos",
};

export function ImportResultsContent({ result }: ImportResultsContentProps) {
  const { handleModal } = useDialogModal();

  const handleClose = () => {
    handleModal({ isOpen: false, element: null, title: "" });
  };

  const handleDownloadSkipped = () => {
    if (result.skipped.length === 0) return;

    const csv = generateSkippedCSV(result.skipped, reasonLabels);
    const now = new Date();
    const date = now.toISOString().split("T")[0];
    const filename = `importacao-ignoradas-${date}.csv`;

    downloadFile(csv, filename);
  };

  const processedText =
    result.processed !== 1
      ? `${result.processed} transacoes processadas com sucesso`
      : `${result.processed} transacao processada com sucesso`;

  const skippedText =
    result.skipped.length !== 1
      ? `${result.skipped.length} transacoes ignoradas:`
      : `${result.skipped.length} transacao ignorada:`;

  return (
    <div className="space-y-4">
      {/* Success Section */}
      <div className="flex items-start gap-3">
        <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
        <div>
          <p className="font-medium text-green-700">{processedText}</p>
        </div>
      </div>

      {/* Skipped Section */}
      {result.skipped.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
            <p className="font-medium text-yellow-700">{skippedText}</p>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 max-h-48 overflow-y-auto">
            <ul className="space-y-1 text-sm">
              {result.skipped.map((item, index) => (
                <li key={index} className="text-yellow-800">
                  Loja {item.store} / Doc {item.docCode} -{" "}
                  {reasonLabels[item.reason]}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Errors Section (if any) */}
      {result.errors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <p className="font-medium text-red-700 mb-2">Erros:</p>
          <ul className="space-y-1 text-sm text-red-600">
            {result.errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex justify-between sm:justify-between">
        {result.skipped.length > 0 && (
          <Button variant="outline" onClick={handleDownloadSkipped}>
            <Download className="h-4 w-4 mr-2" />
            Baixar Ignoradas
          </Button>
        )}
        <Button onClick={handleClose} className={result.skipped.length === 0 ? "ml-auto" : ""}>
          Fechar
        </Button>
      </div>
    </div>
  );
}
